import React, { useEffect, useState } from "react";
import { staffApi, formatBranchMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TableRow, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, X, RefreshCcw, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import GiftCardActions from "./GiftCardActions";
import { templateValidityText } from "@/lib/seriesValidity";
// IMMUTABLE billing brain — per-line base / surcharge / tax
// decomposition (nett vs plus_tax) lives ONLY in BillingEngine.
// See SYSTEM_RULES.md · "Billing Engine Isolation".
import { decomposeLineFinal, lineIsTaxable } from "@/lib/BillingEngine";

/* ------------------- ItemRow: single line with editable adjustment + staff ------------------- */

// Backend `_line_base_price` mirror — used by both the row itself and by
// the parent when it needs to distribute the invoice-wide loyalty
// discount proportionally across the non-gift-card lines.
export function orderLineFinalTotal(item) {
  const listUnit = Number(item.unit_price || 0);
  const qty = Math.max(1, Number(item.quantity) || 1);
  let finalUnit = listUnit - Number(item.discount || 0);
  if (item.adjustment) {
    if (item.adjustment.kind === "override") finalUnit = Math.max(0, Number(item.adjustment.override_price || 0));
    else if (item.adjustment.kind === "special") {
      const dt = item.adjustment.discount_type || "percent";
      const dv = Number(item.adjustment.discount_value || 0);
      finalUnit = dt === "percent" ? Math.max(0, listUnit * (1 - dv / 100)) : Math.max(0, listUnit - dv);
    }
  }
  return finalUnit * qty;
}

export function ItemRow({ index, item, orderId, branch, staffList, specials, editing, disabled, onOpen, onAdjust, onSetStaff, onSetQuantity, onRemove, onClearLoyalty, onRefundLineItem, onRequestPriceOverride, canRefundLineItem = false, canFinalizePrice = true, loyaltyShare = 0, loyaltyPoints = 0, priceMode = "plus_tax", surchargeRate = 0, taxRate = 0, orderOpen = false }) {
  // Post-checkout security (Feb 2026): when the order is closed
  // (`disabled === true`) EVERY line-level field is locked EXCEPT the
  // Care Team assignment. `staffOnly` flips the row into a
  // provider-only edit surface so payroll fixups stay possible even
  // after the payment has been taken.
  const staffOnly = !!disabled;
  const listUnit = Number(item.unit_price || 0);
  const qty = Math.max(1, Number(item.quantity) || 1);
  const listTotal = listUnit * qty;
  const finalTotal = orderLineFinalTotal(item);
  const finalUnit = qty > 0 ? finalTotal / qty : 0;
  const staffSummary = (item.staff_ids || []).map((sid) => (staffList.find((s) => s.id === sid)?.full_name || "—")).join(", ");
  const isRefunded = !!item.refunded;
  // Partial refund detection is redundant-safe: check both the pre-tax
  // `refunded_credit` (used for math) AND the fully-loaded
  // `refund_amount` / `refunded_amount_total` (populated on the sub-row
  // right after a package sub-service refund). Any of them >0 flips the
  // row into "partially refunded" visuals so the user sees the strike +
  // new residual + REFUNDED badge instantly.
  const refundedCredit = Number(item.refunded_credit || 0);
  const partialRefundAmount = Number(item.refunded_amount_total || item.refund_amount || 0);
  const hasAnyRefund = refundedCredit > 0 || partialRefundAmount > 0;
  // Package master with credit accumulated purely from sub-service
  // refunds should NOT show strike-through / REFUNDED badges on itself
  // — the sub-rows own that visual. It just displays the reduced total.
  const isPackageMaster = item.kind === "package";
  const hasSubRefunds = !!item.has_sub_refunds;
  const masterCreditFromSubs = isPackageMaster && hasSubRefunds && !item.refund_id;
  const isPartiallyRefunded = !isRefunded && hasAnyRefund && !masterCreditFromSubs;
  const adjLabel = item.paid_by_series
    ? `Paid by Series (${item.paid_by_series.remaining_after} left)`
    : !item.adjustment ? null : item.adjustment.kind === "override"
    ? `Override → ${formatBranchMoney(branch, item.adjustment.override_price)}`
    : (item.adjustment.name === "Package discount"
        ? (item.adjustment.discount_type === "percent"
            ? `Package discount −${Number(item.adjustment.discount_value || 0).toFixed(1)}%`
            : "Package discount")
        : `Special: ${item.adjustment.name}${item.adjustment.auto ? " · auto" : ""}${item.adjustment.free_addon ? " · free add-on" : ""}`);

  // Commission-only rows sit under a Package master row. They show the
  // employee + list price + discounted "commission portion" but contribute
  // 0 to the invoice total. Render them indented + muted so operators can
  // read the visit's breakdown at a glance.
  const commissionOnly = !!item.commission_only;
  const isGift = item.kind === "gift_card";

  // Loyalty-driven strike-through only kicks in when the invoice actually
  // consumed points AND this specific line is eligible (non-gift, non-
  // commission-only, non-refunded). Voiding the order zeroes
  // `loyaltyPoints` on the parent, so this whole block disappears the
  // moment void is confirmed.
  const rowLoyalty = (loyaltyPoints > 0 && !isGift && !commissionOnly && !isRefunded) ? Number(loyaltyShare || 0) : 0;
  const discountedFinal = Math.max(0, finalTotal - rowLoyalty);

  // Per-line surcharge + tax preview — every rate + the price-mode
  // reversal are computed inside BillingEngine.decomposeLineFinal.
  // Gift-card and commission-only lines are non-taxable per the
  // engine's carve-out, so they never contribute to SC or PPN.
  const showTaxLines = lineIsTaxable(item);
  const { base: lineBase, surcharge: lineSurcharge, tax: linePpn } = decomposeLineFinal({
    discountedFinal,
    priceMode,
    surchargeRate,
    taxRate,
    taxable: showTaxLines,
  });

  // Editable qty — allowed for regular Product & Service lines while
  // the order is still open. Packages stay at 1 (billed as one line),
  // gift-cards are one-per-line, package commission-only rows mirror
  // the master, and any refunded / series-paid row is locked.
  const qtyEditable =
    orderOpen
    && !disabled
    && !isRefunded
    && !isPartiallyRefunded
    && !isPackageMaster
    && !commissionOnly
    && !isGift
    && item.kind !== "series"
    && !item.paid_by_series
    && (item.kind === "product" || item.kind === "service");
  const [qtyDraft, setQtyDraft] = useState(String(qty));
  const [qtySaving, setQtySaving] = useState(false);
  useEffect(() => { setQtyDraft(String(qty)); }, [qty]);
  const commitQty = async () => {
    const n = parseInt(qtyDraft, 10);
    if (isNaN(n) || n < 1) { setQtyDraft(String(qty)); return; }
    if (n === qty) return;
    setQtySaving(true);
    const ok = await onSetQuantity?.(n);
    setQtySaving(false);
    if (ok === false) setQtyDraft(String(qty));
  };

  return (
    <>
      <TableRow
        className={
          (editing ? "bg-amber-50 " : "") +
          (commissionOnly ? "bg-muted/20 " : "") +
          (isPackageMaster ? "bg-amber-50/40 " : "") +
          (isRefunded ? "bg-rose-50/50 " : "")
        }
        data-testid={`item-row-${index}`}
        onClick={(isRefunded) ? undefined : onOpen}
        style={{ cursor: (isRefunded) ? "default" : "pointer" }}
      >
        <TableCell className={commissionOnly ? "pl-8" : undefined}>
          <div className={"text-sm " + (isPackageMaster ? "font-semibold text-amber-900" : "font-medium") + (isRefunded ? " line-through text-muted-foreground" : "")}>
            {isPackageMaster ? "📦 " : (commissionOnly ? "↳ " : "")}{item.name}
            {item.beneficiary_customer_name && (
              <span className="text-muted-foreground font-normal"> — {item.beneficiary_customer_name}</span>
            )}
          </div>
          {/* Jun 2026 · package substitution re-price. The package line
              carries the swapped-in treatment + the rupiah movement so
              the guest can see why the total differs from the catalog
              price. */}
          {isPackageMaster && Number(item.meta?.substitution_delta || 0) !== 0 && (
            <div
              className="mt-1 text-[11px] text-amber-800"
              data-testid={`item-substitution-note-${index}`}
            >
              substituted: {(item.meta?.substituted_services || []).map((s) => s.name).join(", ") || "—"}
              {" · "}
              {Number(item.meta.substitution_delta) > 0 ? "+" : "−"}
              {formatBranchMoney(branch, Math.abs(Number(item.meta.substitution_delta)))}
              {Number(item.meta?.package_list_price || 0) > 0 && (
                <span className="text-muted-foreground">
                  {" "}(catalog {formatBranchMoney(branch, item.meta.package_list_price)})
                </span>
              )}
            </div>
          )}
          {/* Iter-377 · Price override approval badges. Pending =
              Edit-only staff raised a request; original price stays
              active until a Delete-permission holder approves. Rejected
              = the last manager rejected the override; the badge
              persists until the operator either files a fresh request
              or re-opens the row. */}
          {item.pending_price_override && (
            <div className="mt-1 flex items-center gap-2 text-[10px]" data-testid={`item-override-pending-${index}`}>
              <Badge variant="outline" className="rounded-full bg-amber-100 text-amber-800 border-amber-300 text-[10px] font-bold tracking-wider uppercase">
                Pending Approval
              </Badge>
              <span className="text-amber-700">
                {formatBranchMoney(branch, item.pending_price_override.original_price)}
                {" → "}
                <b>{formatBranchMoney(branch, item.pending_price_override.requested_price)}</b>
              </span>
              {item.pending_price_override.requested_by_name && (
                <span className="text-muted-foreground">by {item.pending_price_override.requested_by_name}</span>
              )}
            </div>
          )}
          {!item.pending_price_override && item.last_price_override_rejection && (
            <div className="mt-1 flex items-center gap-2 text-[10px]" data-testid={`item-override-rejected-${index}`}>
              <Badge variant="outline" className="rounded-full bg-rose-100 text-rose-800 border-rose-300 text-[10px] font-bold tracking-wider uppercase">
                Rejected
              </Badge>
              <span className="text-rose-700">
                override {formatBranchMoney(branch, item.last_price_override_rejection.requested_price)}
                {" denied"}
              </span>
              {item.last_price_override_rejection.rejected_by_name && (
                <span className="text-muted-foreground">by {item.last_price_override_rejection.rejected_by_name}</span>
              )}
              {item.last_price_override_rejection.rejected_reason ? (
                <span className="text-muted-foreground">· &ldquo;{item.last_price_override_rejection.rejected_reason}&rdquo;</span>
              ) : null}
            </div>
          )}
          {isRefunded && (
            <div className="mt-1 flex items-center gap-2 text-[10px]" data-testid={`item-refunded-badge-${index}`}>
              <Badge variant="outline" className="rounded-full bg-rose-100 text-rose-800 border-rose-200 text-[10px] font-bold tracking-wider uppercase">
                REFUNDED
              </Badge>
              {item.refund_amount != null && (
                <span className="text-rose-700">−{formatBranchMoney(branch, item.refund_amount)}</span>
              )}
              {item.refunded_by_name && (
                <span className="text-muted-foreground">by {item.refunded_by_name}</span>
              )}
            </div>
          )}
          {isPartiallyRefunded && !masterCreditFromSubs && (
            <div className="mt-1 flex items-center gap-2 text-[10px]" data-testid={`item-partial-refund-badge-${index}`}>
              <Badge variant="outline" className="rounded-full bg-amber-100 text-amber-800 border-amber-200 text-[10px] font-bold tracking-wider uppercase">
                PARTIAL REFUND
              </Badge>
              <span className="text-amber-700">−{formatBranchMoney(branch, partialRefundAmount)}</span>
              {item.refunded_by_name && (
                <span className="text-muted-foreground">by {item.refunded_by_name}</span>
              )}
            </div>
          )}
          {item.appointment_id && <div className="text-[10px] text-muted-foreground">Booking #{String(item.appointment_id).slice(0, 8)}</div>}
          {(item.issued_gift_card_codes || []).length > 0 && (
            <div className="text-[10px] font-mono text-emerald-700" data-testid={`item-gift-codes-${index}`}>
              {item.issued_gift_card_codes.map((code, ci) => (
                <div key={code} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span>{ci === 0 ? "Voucher:" : ""} {code}</span>
                  <GiftCardActions code={code} data-testid-prefix={`item-gift-${index}-${ci}`} />
                </div>
              ))}
            </div>
          )}
          {item.paid_by_series && (
            <div className="text-[10px] text-sky-700" data-testid={`item-series-paid-${index}`}>Paid by series · {item.paid_by_series.series_name}</div>
          )}
          {item.kind === "series" && item.series_validity && (
            <div className="text-[10px] text-sky-700 flex items-center gap-1" data-testid={`item-series-validity-${index}`}>
              <CalendarClock className="w-3 h-3" /> {templateValidityText(item.series_validity)}
            </div>
          )}
          {commissionOnly && <div className="text-[10px] uppercase tracking-wider text-muted-foreground">commission portion · not billed</div>}
        </TableCell>
        <TableCell className="text-xs">{staffSummary || <span className="text-muted-foreground italic">{isPackageMaster ? "—" : "Unassigned"}</span>}</TableCell>
        <TableCell className="text-center">
          {qtyEditable ? (
            <Input
              type="number"
              min={1}
              max={999}
              value={qtyDraft}
              disabled={qtySaving}
              onChange={(e) => setQtyDraft(e.target.value)}
              onBlur={commitQty}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.currentTarget.blur(); }
                if (e.key === "Escape") { setQtyDraft(String(qty)); e.currentTarget.blur(); }
              }}
              onClick={(e) => e.stopPropagation()}
              className="h-8 w-16 mx-auto text-center"
              data-testid={`item-qty-input-${index}`}
            />
          ) : (
            <span data-testid={`item-qty-${index}`}>{qty}</span>
          )}
        </TableCell>
        <TableCell className={"text-right " + (isRefunded ? "line-through text-muted-foreground" : "")}>{formatBranchMoney(branch, listTotal)}</TableCell>
        <TableCell className="text-xs">
          <div className="space-y-0.5">
            {adjLabel && <div className={"text-primary" + (isRefunded ? " line-through text-muted-foreground" : "")}>{adjLabel}</div>}
            {rowLoyalty > 0 && (
              <div className="text-amber-700 inline-flex items-center gap-1" data-testid={`item-loyalty-adj-${index}`}>
                <span>Loyalty redeem −{formatBranchMoney(branch, rowLoyalty)}</span>
                {orderOpen && onClearLoyalty && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onClearLoyalty(); }}
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-amber-300 text-amber-700 hover:bg-amber-100 hover:text-amber-900"
                    title="Cancel loyalty redemption for this bill"
                    data-testid={`item-loyalty-clear-${index}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                )}
              </div>
            )}
            {!adjLabel && rowLoyalty <= 0 && <span className="text-muted-foreground">—</span>}
          </div>
        </TableCell>
        <TableCell className={"text-right " + (commissionOnly ? "text-muted-foreground" : "font-medium")}>
          {isRefunded ? (
            <div className="flex flex-col items-end gap-0.5" data-testid={`item-final-refunded-${index}`}>
              <span className="text-muted-foreground line-through text-xs" data-testid={`item-final-strike-${index}`}>{formatBranchMoney(branch, finalTotal)}</span>
              <span className="text-rose-700 font-semibold" data-testid={`item-final-new-${index}`}>{formatBranchMoney(branch, 0)}</span>
            </div>
          ) : masterCreditFromSubs ? (
            // Package master whose credit came only from sub-service
            // refunds. Show the reduced residual plainly — no strike,
            // no badge (spec 2b: header total simply reflects the sum
            // of its services after refund).
            <span data-testid={`item-final-master-adjusted-${index}`}>{formatBranchMoney(branch, Math.max(0, finalTotal - refundedCredit))}</span>
          ) : isPartiallyRefunded ? (
            <div className="flex flex-col items-end gap-0.5" data-testid={`item-final-partial-${index}`}>
              <span className="text-muted-foreground line-through text-xs" data-testid={`item-final-strike-${index}`}>{formatBranchMoney(branch, finalTotal)}</span>
              <span className="text-rose-700 font-semibold" data-testid={`item-final-new-${index}`}>{formatBranchMoney(branch, Math.max(0, finalTotal - (refundedCredit || partialRefundAmount)))}</span>
            </div>
          ) : rowLoyalty > 0 ? (
            <div className="flex flex-col items-end gap-0.5" data-testid={`item-final-loyalty-${index}`}>
              <span className="text-muted-foreground line-through text-xs" data-testid={`item-final-strike-${index}`}>{formatBranchMoney(branch, finalTotal)}</span>
              <span className="text-emerald-700" data-testid={`item-final-discounted-${index}`}>{formatBranchMoney(branch, discountedFinal)}</span>
            </div>
          ) : (
            <span data-testid={`item-final-${index}`}>{formatBranchMoney(branch, finalTotal)}</span>
          )}
          {/* Referenced so eslint doesn't complain when only the unit
              math is used implicitly; kept in case a future edit needs
              per-unit display. */}
          <span className="hidden">{finalUnit}{lineBase}{lineSurcharge}{linePpn}</span>
        </TableCell>
        <TableCell className="text-right">
          {canRefundLineItem && !isRefunded ? (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 gap-1 text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800"
              onClick={(e) => { e.stopPropagation(); onRefundLineItem?.(index); }}
              data-testid={`item-refund-btn-${index}`}
              title="Refund this line — past business day"
            >
              <RefreshCcw className="w-3 h-3" />
              Refund Item
            </Button>
          ) : (item.refund_locked || item.refunded_at || hasAnyRefund) && !isRefunded ? (
            // Refunded once (full or partial) → the row is permanently
            // locked from further refund events (one-time-only rule).
            // Show a subtle chip in place of the button so operators see
            // WHY they can't hit "Refund Item" again.
            <Badge
              variant="outline"
              className="rounded-full text-[10px] bg-slate-100 text-slate-600 border-slate-300 font-medium"
              data-testid={`item-refund-locked-${index}`}
              title="Refund is one-time-only — this line is permanently locked."
            >
              Refund locked
            </Badge>
          ) : (
            <Button size="icon" variant="ghost" className="h-7 w-7" disabled={disabled || isRefunded} onClick={(e) => { e.stopPropagation(); onRemove(); }} data-testid={`item-remove-${index}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          )}
        </TableCell>
      </TableRow>
      {editing && (
        <TableRow className="bg-amber-50" onClick={(e) => e.stopPropagation()}>
          <TableCell colSpan={7} className="pt-2 pb-4">
            <ItemAdjustmentEditor
              item={item}
              orderId={orderId}
              itemIndex={index}
              staffList={staffList}
              branch={branch}
              onAdjust={onAdjust}
              onSetStaff={onSetStaff}
              onDone={onOpen}
              onRequestPriceOverride={onRequestPriceOverride}
              canFinalizePrice={canFinalizePrice}
              staffOnly={staffOnly}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function specialPricingLabel(s, branch) {
  const mode = s.pricing_mode || "discount";
  if (mode === "price_ranges") return "price ranges";
  if (mode === "free_addon") return "free add-on";
  return s.discount_type === "percent" ? `${s.discount_value}%` : formatBranchMoney(branch, s.discount_value);
}

function ItemAdjustmentEditor({ item, orderId, itemIndex, staffList, branch, onAdjust, onSetStaff, onDone, onRequestPriceOverride, canFinalizePrice = true, staffOnly = false }) {
  const currentKind = item.adjustment?.kind || "none";
  const [kind, setKind] = useState(currentKind);
  const [specials, setSpecials] = useState([]);
  const [specialsLoading, setSpecialsLoading] = useState(true);
  const [specialId, setSpecialId] = useState(item.adjustment?.special_id || "");
  // Default the override input to EMPTY when there is no prior
  // adjustment — operators MUST type the new price explicitly so a
  // silent "Save" cannot accidentally re-stamp the current list price
  // as an override (which would appear as no-op on the invoice).
  const [overridePrice, setOverridePrice] = useState(
    item.adjustment?.override_price != null ? String(item.adjustment.override_price) : ""
  );
  const [overrideReason, setOverrideReason] = useState(item.adjustment?.reason || "");
  const [staffIds, setStaffIds] = useState(item.staff_ids || []);

  useEffect(() => {
    if (staffOnly) { setSpecialsLoading(false); return; }
    let cancel = false;
    (async () => {
      setSpecialsLoading(true);
      try {
        const { data } = await staffApi.get(`/orders/${orderId}/items/${itemIndex}/eligible-specials`);
        if (cancel) return;
        const list = data || [];
        setSpecials(list);
        setSpecialId((cur) => {
          if (cur && list.some((s) => s.id === cur)) return cur;
          return list[0]?.id || "";
        });
      } catch (e) {
        if (!cancel) setSpecials([]);
      } finally {
        if (!cancel) setSpecialsLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [orderId, itemIndex, staffOnly]);

  const save = async () => {
    // Closed-order guard: only the staff change round-trips. Prices,
    // qty, codes and specials are frozen (backend enforces the same
    // rule with a 409 so a hand-crafted request also can't slip
    // through).
    if (!staffOnly) {
      if (kind === "none") {
        if (await onAdjust(null) === false) return;
      } else if (kind === "special") {
        if (!specialId) { toast.error("Choose a special"); return; }
        if (await onAdjust({ kind: "special", special_id: specialId }) === false) return;
      } else if (kind === "override") {
        const raw = String(overridePrice ?? "").trim();
        if (raw === "") {
          toast.error("Enter the new price (type 0 to make this line free).");
          return;
        }
        const n = parseFloat(raw);
        if (isNaN(n) || n < 0) { toast.error("Override price must be a non-negative number"); return; }
        // Iter-377 · Staff with Edit (not Delete) can only REQUEST
        // the override — the line's original price stays active, a
        // "Pending Approval" badge is painted, and a Delete-permission
        // manager finalises (or rejects) the change from the
        // Approvals queue. Delete-holders keep the direct apply path.
        if (!canFinalizePrice) {
          if (!onRequestPriceOverride) {
            toast.error("Price override requires manager approval — this action is unavailable in this view.");
            return;
          }
          const ok = await onRequestPriceOverride(itemIndex, { new_price: n, reason: overrideReason });
          if (ok === false) return;
          onDone();
          return;
        }
        if (await onAdjust({ kind: "override", override_price: n, reason: overrideReason }) === false) return;
      }
    }
    if (JSON.stringify(staffIds) !== JSON.stringify(item.staff_ids || [])) {
      await onSetStaff(staffIds);
    }
    onDone();
  };

  const addStaff = (sid) => { if (!staffIds.includes(sid)) setStaffIds([...staffIds, sid]); };
  const removeStaff = (sid) => setStaffIds(staffIds.filter((x) => x !== sid));

  return (
    <div className={"grid grid-cols-1 gap-6 px-2 " + (staffOnly ? "" : "lg:grid-cols-2")}>
      {!staffOnly && (
        <div className="space-y-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Adjustment (special or manual override — not both)</p>
          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="radio" checked={kind === "none"}     onChange={() => setKind("none")}     data-testid="adj-kind-none" /> None</label>
            <label className="flex items-center gap-2"><input type="radio" checked={kind === "special"}  onChange={() => setKind("special")}  data-testid="adj-kind-special" /> Special</label>
            {/* A commission-only package child is a derived, not-billed
                row — the guest pays the parent package line, so an
                override belongs there, never here. */}
            {!item.commission_only && (
              <label className="flex items-center gap-2"><input type="radio" checked={kind === "override"} onChange={() => setKind("override")} data-testid="adj-kind-override" /> Price override</label>
            )}
          </div>
          {item.commission_only && (
            <p className="text-[11px] text-muted-foreground" data-testid="adj-override-package-hint">
              Part of a package — not billed on its own. To change what the guest pays, override the package line above.
            </p>
          )}
          {kind === "special" && (
            <div className="space-y-2">
              <Label>Special</Label>
              <Select value={specialId} onValueChange={setSpecialId}>
                <SelectTrigger data-testid="adj-special-select">
                  <SelectValue placeholder={specialsLoading ? "Loading…" : (specials.length ? "Choose a special" : "No specials match this line")} />
                </SelectTrigger>
                <SelectContent>
                  {specials.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} — {specialPricingLabel(s, branch)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!specialsLoading && specials.length === 0 && (
                <p className="text-xs text-muted-foreground">No active special covers this treatment for the booked date/time.</p>
              )}
            </div>
          )}
          {kind === "override" && (
            <>
              <div>
                <Label>Override price (per unit, before surcharge + tax)</Label>
                <Input
                  type="number"
                  min={0}
                  value={overridePrice}
                  onChange={(e) => setOverridePrice(e.target.value)}
                  placeholder={`List price: ${formatBranchMoney(branch, item.unit_price)} — type the new price (0 to make this line free)`}
                  data-testid="adj-override-price"
                />
              </div>
              <div>
                <Label>Reason (audited)</Label>
                <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="Loyal customer, price-match, etc." data-testid="adj-override-reason" />
              </div>
              {!canFinalizePrice && (
                <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid="adj-override-approval-hint">
                  Price overrides require a manager with <b>Order/POS · Delete</b>. Saving will send this to the Approvals queue — the line stays at its original price until approved.
                </p>
              )}
            </>
          )}
        </div>
      )}

      <div className="space-y-3">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Employee / Commission to
          {staffOnly && (
            <span className="ml-2 rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 text-[10px] normal-case tracking-normal" data-testid="closed-order-staff-hint">
              Care Team still editable · Closed order
            </span>
          )}
        </p>
        <div className="flex flex-wrap gap-1">
          {staffIds.length === 0 && <span className="text-xs text-muted-foreground italic">No staff assigned yet</span>}
          {staffIds.map((sid) => {
            const s = staffList.find((x) => x.id === sid);
            return (
              <Badge key={sid} variant="outline" className="rounded-full text-xs flex items-center gap-1">
                {s?.full_name || "Unknown"}
                <button onClick={() => removeStaff(sid)} className="hover:text-rose-600" data-testid={`adj-remove-staff-${sid}`}><X className="w-3 h-3" /></button>
              </Badge>
            );
          })}
        </div>
        <Select value="" onValueChange={addStaff}>
          <SelectTrigger data-testid="adj-add-staff-select"><SelectValue placeholder="+ Add staff" /></SelectTrigger>
          <SelectContent>
            {staffList.filter((s) => !staffIds.includes(s.id)).map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" onClick={onDone} data-testid="adj-cancel">Cancel</Button>
          <Button size="sm" onClick={save} data-testid="adj-save">Save</Button>
        </div>
      </div>
    </div>
  );
}
